import React, { useEffect, useState, useMemo } from "react";
import {
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import config from "../../../config";
import { authFetch } from "../../../utils/Security/authFetch";
import "../../../Style/DashboardChart.css";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

type EntityType = "incident" | "change" | "task" | "problem" | "rma";

type MonthlyEntityCountDTO = {
  month: string;
  type: EntityType;
  count: number;
};

type ChartData = {
  month: string;
  incident?: number;
  change?: number;
  task?: number;
  problem?: number;
  rma?: number;
  totalTickets?: number;
};

const DISPLAY_NAMES: Record<string, string> = {
  incident: "Incident",
  change: "Change",
  task: "Task",
  problem: "Problem",
  rma: "RMA",
  totalTickets: "Total Tickets",
};

// Helper: load CSS variable colors
const getColors = (): Record<EntityType, string> => {
  const styles = getComputedStyle(document.body);
  return {
    incident: styles.getPropertyValue("--incident-color").trim(),
    change: styles.getPropertyValue("--change-color").trim(),
    task: styles.getPropertyValue("--task-color").trim(),
    problem: styles.getPropertyValue("--problem-color").trim(),
    rma: styles.getPropertyValue("--rma-color").trim(),
  };
};
interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    dataKey: string;
    color?: string;
    value?: number;
  }>;
  label?: string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
}) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <ul>
          {payload.map((entry) => (
            <li
              key={entry.dataKey}
              style={{ color: entry.color || "#333", fontWeight: 500 }}
            >
              {DISPLAY_NAMES[entry.dataKey] || entry.dataKey}:{" "}
              {entry.value === 0.1 ? 0 : entry.value?.toLocaleString()}
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return null;
};

const DashboardChart: React.FC = () => {
  const [data, setData] = useState<ChartData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [useLogScale, setUseLogScale] = useState(true);
  const [showTotalLine, setShowTotalLine] = useState(true);
  const [isAdmin, setIsAdmin] = useState(true);
  const [isForTelebras, setisForTelebras] = useState(true);

  const [selectedYear, setSelectedYear] = useState<string | null>("2026");
  const [colors, setColors] = useState<Record<EntityType, string>>(getColors());

  // Watch for dark mode toggle → update colors
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setColors(getColors());
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  // Export Line dataset as PDF (just the chart)
  const exportPDF = async () => {
    alert(1);
    const chart = document.querySelector(
      ".dashboard-chart-card",
    ) as HTMLElement;
    if (!chart) return;

    const canvas = await html2canvas(chart, {
      scale: 3, // crisp text
      backgroundColor: "#ffffff", // REMOVE purple background
      useCORS: true,
    });

    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("landscape", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const imgWidth = pageWidth - 20;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    pdf.addImage(
      imgData,
      "PNG",
      10,
      (pageHeight - imgHeight) / 2,
      imgWidth,
      imgHeight,
    );

    pdf.save("monthly-dashboard-chart.pdf");
  };
  const exportExcel = () => {
    const headers = [
      "Month",
      "Incident",
      "Change",
      "Task",
      "Problem",
      "RMA",
      "TotalTickets",
    ];
    const rows = chartData.map((row) => [
      row.month,
      row.incident && row.incident >= 1 ? row.incident : 0,
      row.change && row.change >= 1 ? row.change : 0,
      row.task && row.task >= 1 ? row.task : 0,
      row.problem && row.problem >= 1 ? row.problem : 0,
      row.rma && row.rma >= 1 ? row.rma : 0,
      row.totalTickets && row.totalTickets >= 1 ? row.totalTickets : 0,
    ]);
    const csvContent = [headers, ...rows].map((r) => r.join(",")).join("\n");

    const blob = new Blob(["﻿" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = "chart-data.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    const isAdmin = localStorage.getItem("isAdmin") === "true";
    const userId = localStorage.getItem("userid");
    const isForTelebras = localStorage.getItem("isForTelebras") === "true";
    const isForPetrobras = localStorage.getItem("isForPetrobras") === "true";
    const isForNilesat = localStorage.getItem("isForNilesat") === "true";

    setIsAdmin(isAdmin);

    const isNormalUser = !isAdmin;
    const shouldSendUserId =
      (isNormalUser || isForTelebras || isForPetrobras || isForNilesat) && userId;

    const suffix = shouldSendUserId ? `?userId=${userId}` : "";

    const endpoint =
       isAdmin && (isForTelebras || isForPetrobras || isForNilesat)
        ? "/api/dashboard/telebrasmonthly-counts"
        : "/api/dashboard/monthly-counts";

    authFetch(`${config.apiBaseUrl}${endpoint}${suffix}`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch");
        return res.json();
      })
      .then((raw: MonthlyEntityCountDTO[]) => {
        const grouped: Record<string, ChartData> = {};
        raw.forEach(({ month, type, count }) => {
          if (!month) return;
          if (!grouped[month]) grouped[month] = { month };
          grouped[month][type] = count;
        });
        const sorted = Object.values(grouped).sort((a, b) =>
          a.month.localeCompare(b.month),
        );
        setData(sorted);
      })
      .catch((err) => setError(err.message || "Unknown error"))
      .finally(() => setLoading(false));
  }, []);

  const ENTITY_TYPES: EntityType[] = isAdmin
    ? ["incident", "change", "task", "problem", "rma"]
    : ["incident", "change", "task", "rma"];

  const availableYears = useMemo(() => {
    const years = new Set<string>();
    data.forEach((item) => {
      const year = item.month.split("-")[0];
      years.add(year);
    });
    return Array.from(years).sort();
  }, [data]);

  const chartData = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = String(now.getMonth() + 1).padStart(2, "0");

    const filtered = data.filter((d) => {
      const [year, month] = d.month.split("-");
      if (selectedYear && year !== selectedYear) return false;
      if (parseInt(year) > currentYear) return false;
      if (parseInt(year) === currentYear && month > currentMonth) return false;
      return true;
    });

    return filtered.map((item) => {
      const fixed = { month: item.month } as ChartData;
      let total = 0;

      ENTITY_TYPES.forEach((key) => {
        let val = item[key] ?? 0;
        if (useLogScale && val === 0) val = 0.1;
        fixed[key] = val;
        total += val;
      });

      fixed.totalTickets = Math.round(total);
      return fixed;
    });
  }, [data, useLogScale, selectedYear, ENTITY_TYPES]);

  return (
    <div className="dashboard-chart-container">
      <div className="dashboard-chart-card">
        <h2>Monthly Record Counts</h2>

        {/* Dropdown Controls */}
        <div className="dashboard-chart-controls">
          <select
            value={selectedYear || ""}
            onChange={(e) =>
              setSelectedYear(e.target.value === "" ? null : e.target.value)
            }
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
            <option value="">All Years</option>
          </select>

          <select
            value={useLogScale ? "log" : "linear"}
            onChange={(e) => setUseLogScale(e.target.value === "log")}
          >
            <option value="linear">Linear</option>
            <option value="log">Logarithmic</option>
          </select>

          <select
            value={showTotalLine ? "show" : "hide"}
            onChange={(e) => setShowTotalLine(e.target.value === "show")}
          >
            <option value="show">Show Total Line</option>
            <option value="hide">Hide Total Line</option>
          </select>
          <button onClick={exportPDF}>Export Line as PDF</button>
          <button onClick={exportExcel}>Export Data as Excel</button>
        </div>

        {loading && <p className="loading-text">Loading data...</p>}
        {error && <p className="error-text">Error: {error}</p>}

        {!loading && !error && (
          <ResponsiveContainer width="100%" height={440}>
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 20, bottom: 20 }}
            >
              <defs>
                {ENTITY_TYPES.map((key) => (
                  <linearGradient
                    key={key}
                    id={`${key}Gradient`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="0%"
                      stopColor={colors[key]}
                      stopOpacity={0.9}
                    />
                    <stop
                      offset="100%"
                      stopColor={colors[key]}
                      stopOpacity={0.6}
                    />
                  </linearGradient>
                ))}
              </defs>

              <XAxis dataKey="month" tick={{ fill: "#4832a4" }} stroke="#ccc" />
              <YAxis
                scale={useLogScale ? "log" : "linear"}
                domain={useLogScale ? [0.1, "auto"] : [0, "auto"]}
                allowDecimals={false}
                label={{
                  value: "Count",
                  angle: -90,
                  position: "insideLeft",
                  fill: "#c0b0ffff",
                }}
                tick={{ fill: "#555" }}
                stroke="#ccc"
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                verticalAlign="top"
                height={36}
                wrapperStyle={{ color: "#616161ff" }}
              />
              {showTotalLine && (
                <Line
                  type="monotone"
                  dataKey="totalTickets"
                  name={DISPLAY_NAMES.totalTickets}
                  stroke="#c5aef7"
                  strokeWidth={3}
                  dot={{ r: 4 }}
                />
              )}
              {ENTITY_TYPES.map((key) => {
                const isDarkMode =
                  document.body.classList.contains("dark-mode");
                return (
                  <Bar
                    key={key}
                    dataKey={key}
                    name={DISPLAY_NAMES[key]}
                    fill={isDarkMode ? colors[key] : `url(#${key}Gradient)`} // solid in dark
                    radius={[6, 6, 0, 0]}
                  />
                );
              })}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default DashboardChart;
